import React from 'react';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { AccessDenied } from '@/components/AccessDenied';
import { PermissionKey } from '@/types/permissions';

interface WithPermissionsOptions {
  requiredPermissions: PermissionKey[];
  requireAll?: boolean; // If true, user must have ALL permissions, otherwise ANY
  fallback?: React.ComponentType<{
    requiredPermissions: string[];
    showRoleSwitcher?: boolean;
  }>;
  showRoleSwitcher?: boolean;
}

/**
 * Higher-order component that protects a component with permission checks
 * @param Component - Component to protect
 * @param options - Permission options
 * @returns Protected component
 */
export function withPermissions<P extends object>(
  Component: React.ComponentType<P>,
  options: WithPermissionsOptions
) {
  const {
    requiredPermissions,
    fallback: Fallback = AccessDenied,
    showRoleSwitcher = true,
  } = options;

  return function ProtectedComponent(props: P) {
    const hasAccess = usePermissionAccess(requiredPermissions);

    if (!hasAccess) {
      return (
        <Fallback
          requiredPermissions={requiredPermissions}
          showRoleSwitcher={showRoleSwitcher}
        />
      );
    }

    return <Component {...props} />;
  };
}

/**
 * Helper function to create a protected component quickly
 * @param Component - Component to protect
 * @param requiredPermissions - Array of required permissions
 * @param requireAll - If true, user must have ALL permissions
 * @returns Protected component
 */
export function requirePermissions<P extends object>(
  Component: React.ComponentType<P>,
  requiredPermissions: PermissionKey[],
  requireAll: boolean = false
) {
  return withPermissions(Component, {
    requiredPermissions,
    requireAll,
  });
}
